<template>
  <div class="rt-root">
    <div class="rt-page">
      <!-- Page header -->
      <div class="rt-head">
        <h1 class="rt-title">
          {{ $t("RatingTitle") || "Tekshiruv jarayonini baholash" }}
          <span class="rt-pill">{{ $t("RatingNew") || "Yangi baholash" }}</span>
        </h1>
        <p class="rt-sub">
          {{
            $t("RatingDesc") ||
              "Sizning fikr-mulohazangiz davlat nazorat organlari faoliyatini takomillashtirish uchun muhim. Baholash anonim emas, lekin xolis va konstruktiv mulohaza yuborish tavsiya etiladi."
          }}
        </p>
      </div>

      <!-- Step 1: Search -->
      <section class="rt-card">
        <div class="rt-card-head">
          <h2>
            <span class="rt-step">1</span>
            {{ $t("RatingStep1") || "Tekshiruvni aniqlash" }}
          </h2>
          <span class="rt-hint">
            {{ $t("RatingStep1Hint") || "Quyidagi usullardan birini tanlang" }}
          </span>
        </div>
        <div class="rt-card-body">
          <div class="rt-tabs">
            <button
              class="rt-tab"
              :class="{ active: searchTab === 'inspector' }"
              @click="searchTab = 'inspector'"
            >
              <i class="fas fa-search"></i>
              {{ $t("RatingByInspector") || "Tekshiruvchi FIO bo'yicha" }}
            </button>
            <button
              class="rt-tab"
              :class="{ active: searchTab === 'number' }"
              @click="searchTab = 'number'"
            >
              <i class="fas fa-hashtag"></i>
              {{ $t("RatingByNumber") || "Tekshiruv raqami bo'yicha" }}
            </button>
          </div>

          <div class="rt-search-row">
            <div v-show="searchTab === 'inspector'" class="rt-field">
              <label>
                {{ $t("InspectorFIO") || "Tekshiruvchining FIO" }}
                <span class="req">*</span>
              </label>
              <div class="rt-input-wrap">
                <i class="fas fa-user lead"></i>
                <input
                  v-model="inspectorQuery"
                  type="text"
                  :placeholder="$t('InspectorFIOPh') || 'Masalan: Falonchiyev Pistonchi'"
                />
                <button class="rt-go" @click="searchInspector">
                  {{ $t("Search") || "Qidirish" }}
                </button>
              </div>
            </div>

            <div v-show="searchTab === 'number'" class="rt-field">
              <label>{{ $t("InspectionNumber") || "Tekshiruv raqami" }}</label>
              <div class="rt-input-wrap">
                <i class="fas fa-list lead"></i>
                <input
                  v-model="numberQuery"
                  type="text"
                  placeholder="TX-2026-000000"
                />
                <button class="rt-go" @click="searchByNumber">
                  {{ $t("Search") || "Qidirish" }}
                </button>
              </div>
            </div>
          </div>

          <!-- Found inspector card -->
          <div v-if="foundInspector" class="rt-found">
            <div class="rt-av">{{ foundInspector.initials }}</div>
            <div class="rt-info">
              <div class="rt-nm">{{ foundInspector.name }}</div>
              <div class="rt-org">{{ foundInspector.org }}</div>
            </div>
            <div class="rt-badge">{{ $t("Found") || "Topildi" }}</div>
          </div>

          <!-- Inspection list -->
          <div v-if="inspections.length" class="rt-insp-wrap">
            <label class="rt-sublabel">
              {{
                ($t("RatingInspectionList") ||
                  "Ushbu tekshiruvchi tomonidan o'tkazilgan tekshiruvlar")
              }}
              ({{ inspections.length }})
            </label>
            <div class="rt-insp-list">
              <div
                v-for="ins in inspections"
                :key="ins.id"
                class="rt-insp"
                :class="{ selected: selectedInspection === ins.id }"
                @click="selectedInspection = ins.id"
              >
                <div class="rt-radio"></div>
                <div class="rt-main">
                  <div class="rt-code">{{ ins.code }}</div>
                  <div class="rt-desc">{{ ins.desc }}</div>
                </div>
                <div class="rt-date">
                  <span class="rt-d-label">
                    {{ $t("Otkazilgan") || "O'tkazilgan" }}
                  </span>
                  <span class="rt-d-v">{{ ins.date }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Step 2: Rate -->
      <section class="rt-card">
        <div class="rt-card-head">
          <h2>
            <span class="rt-step">2</span>
            {{ $t("RatingStep2") || "Baholash" }}
          </h2>
          <span class="rt-hint">
            {{ $t("RatingStep2Hint") || "5 ballik tizim · Izoh majburiy emas" }}
          </span>
        </div>
        <div class="rt-card-body">
          <div class="rt-rate-grid">
            <!-- Inspector block -->
            <div class="rt-rate-block">
              <div class="rt-who">
                <div class="rt-who-icon"><i class="fas fa-user"></i></div>
                <div>
                  <div class="rt-who-lbl">
                    {{ $t("Inspector") || "Tekshiruvchi" }}
                  </div>
                  <div class="rt-who-nm">
                    {{ foundInspector ? foundInspector.name : "—" }}
                  </div>
                </div>
              </div>
              <div>
                <div class="rt-stars">
                  <button
                    v-for="n in 5"
                    :key="'i' + n"
                    class="rt-star"
                    :class="{ filled: n <= inspectorRating }"
                    @click="inspectorRating = n"
                    @mouseenter="hoverInspector = n"
                    @mouseleave="hoverInspector = 0"
                  >
                    <i class="fas fa-star"></i>
                  </button>
                </div>
                <div class="rt-rate-label">
                  {{
                    $t("InspectorRateLbl") ||
                      "Tekshiruvchining xulqi, kompetensiyasi"
                  }}
                  <strong>{{ inspectorRating }} / 5</strong>
                </div>
              </div>
              <div>
                <textarea
                  v-model="inspectorComment"
                  class="rt-comment"
                  maxlength="500"
                  :placeholder="
                    $t('InspectorCommentPh') ||
                      'Tekshiruvchi haqida fikringizni yozing: muloqot uslubi, professionalligi, xolisligi va h.k.'
                  "
                ></textarea>
                <div class="rt-cc">
                  {{ inspectorComment.length }} / 500
                  {{ $t("Symbol") || "belgi" }}
                </div>
              </div>
            </div>

            <!-- Inspection block -->
            <div class="rt-rate-block">
              <div class="rt-who">
                <div class="rt-who-icon"><i class="fas fa-clipboard-check"></i></div>
                <div>
                  <div class="rt-who-lbl">
                    {{ $t("InspectionProcess") || "Tekshiruv jarayoni" }}
                  </div>
                  <div class="rt-who-nm">
                    {{ selectedInspectionCode || "—" }}
                  </div>
                </div>
              </div>
              <div>
                <div class="rt-stars">
                  <button
                    v-for="n in 5"
                    :key="'p' + n"
                    class="rt-star"
                    :class="{ filled: n <= inspectionRating }"
                    @click="inspectionRating = n"
                  >
                    <i class="fas fa-star"></i>
                  </button>
                </div>
                <div class="rt-rate-label">
                  {{
                    $t("InspectionRateLbl") ||
                      "Tekshiruv samaradorligi va adolatliligi"
                  }}
                  <strong>{{ inspectionRating }} / 5</strong>
                </div>
              </div>
              <div>
                <textarea
                  v-model="inspectionComment"
                  class="rt-comment"
                  maxlength="500"
                  :placeholder="
                    $t('InspectionCommentPh') ||
                      'Tekshiruv jarayoni haqida fikringiz: jarayon shaffofligi, qonuniyligi, samaradorligi va h.k.'
                  "
                ></textarea>
                <div class="rt-cc">
                  {{ inspectionComment.length }} / 500
                  {{ $t("Symbol") || "belgi" }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="rt-actions">
          <div class="rt-note">
            <i class="fas fa-info-circle"></i>
            {{
              $t("RatingNote") ||
                "Baholash yuborilgandan keyin uni o'zgartirish mumkin emas"
            }}
          </div>
          <div class="rt-btns">
            <button class="rt-btn ghost" @click="cancel">
              {{ $t("Cancel") || "Bekor qilish" }}
            </button>
            <button class="rt-btn primary" @click="submit">
              {{ $t("Submit") || "Yuborish" }}
              <i class="fas fa-arrow-right"></i>
            </button>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
export default {
  name: "RatingEdit",
  data() {
    return {
      searchTab: "inspector",
      inspectorQuery: "Falonchiyev Pistonchi Eshmatovich",
      numberQuery: "",
      foundInspector: {
        initials: "FP",
        name: "Falonchiyev Pistonchi Eshmatovich",
        org:
          "Davlat soliq qo'mitasi · Toshkent shahar boshqarmasi · Bosh inspektor",
      },
      inspections: [
        {
          id: 1,
          code: "TX-2026-001847",
          desc: "Reja asosida soliq tekshiruvi · 2026-yil 1-chorak",
          date: "14.05.2026",
        },
        {
          id: 2,
          code: "TX-2026-001203",
          desc: "Hujjatli tekshiruv · Murojaat asosida",
          date: "22.03.2026",
        },
        {
          id: 3,
          code: "TX-2025-009881",
          desc: "Reja bo'yicha kompleks tekshiruv",
          date: "11.11.2025",
        },
      ],
      selectedInspection: 1,
      inspectorRating: 4,
      inspectionRating: 5,
      hoverInspector: 0,
      inspectorComment:
        "Tekshiruvchi o'z ishini puxta bajardi, savollarga aniq javob berdi. Faqat ba'zi protseduralarni tushuntirishda biroz qisqalik bo'ldi.",
      inspectionComment:
        "Tekshiruv qonun talablariga to'liq mos ravishda olib borildi. Barcha protseduralar oldindan tushuntirildi.",
    };
  },
  computed: {
    selectedInspectionCode() {
      const ins = this.inspections.find((i) => i.id === this.selectedInspection);
      return ins ? ins.code : "";
    },
  },
  methods: {
    searchInspector() {
      // hook for inspector search API
    },
    searchByNumber() {
      // hook for inspection-number search API
    },
    cancel() {
      this.$router.push({ name: "rating" });
    },
    submit() {
      // hook for submit API
    },
  },
};
</script>

<style lang="scss" scoped>
.rt-root {
  --rt-bg: #f5f3ee;
  --rt-card: #ffffff;
  --rt-ink: #0e1726;
  --rt-ink-soft: #475569;
  --rt-ink-muted: #94a3b8;
  --rt-line: #e2e5ea;
  --rt-primary: #1e4a8a;
  --rt-primary-dark: #163866;
  --rt-accent: #c9a14a;
  --rt-accent-soft: #f4e9cc;
  --rt-success: #1f7a4d;
  --rt-danger: #b43a3a;

  background: var(--rt-bg);
  color: var(--rt-ink);
  min-height: calc(100vh - 80px);
  font-size: 15px;
  background-image: radial-gradient(
    circle at 1px 1px,
    rgba(14, 23, 38, 0.05) 1px,
    transparent 0
  );
  background-size: 24px 24px;
}
.rt-page {
  max-width: 960px;
  margin: 0 auto;
  padding: 28px 24px 80px;
}

/* head */
.rt-head {
  padding: 12px 4px 16px;
}
.rt-title {
  font-size: 30px;
  font-weight: 700;
  letter-spacing: -0.01em;
  line-height: 1.18;
  display: flex;
  align-items: baseline;
  gap: 12px;
  flex-wrap: wrap;
}
.rt-pill {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.18em;
  color: var(--rt-primary);
  background: #e5ecf7;
  padding: 5px 10px;
  border-radius: 4px;
  text-transform: uppercase;
  transform: translateY(-4px);
}
.rt-sub {
  margin-top: 8px;
  color: var(--rt-ink-soft);
  max-width: 780px;
  font-size: 14px;
}

/* card */
.rt-card {
  background: var(--rt-card);
  border: 1px solid var(--rt-line);
  border-radius: 14px;
  box-shadow: 0 1px 2px rgba(14, 23, 38, 0.04);
  overflow: hidden;
  & + .rt-card {
    margin-top: 22px;
  }
}
.rt-card-head {
  padding: 18px 22px;
  border-bottom: 1px solid var(--rt-line);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  h2 {
    font-size: 17px;
    font-weight: 700;
    letter-spacing: -0.01em;
    display: flex;
    align-items: center;
    gap: 10px;
    margin: 0;
  }
}
.rt-step {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: var(--rt-ink);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
}
.rt-hint {
  font-size: 12px;
  color: var(--rt-ink-muted);
}
.rt-card-body {
  padding: 22px;
}

/* tabs */
.rt-tabs {
  display: flex;
  background: #f0eee7;
  border: 1px solid var(--rt-line);
  border-radius: 10px;
  padding: 4px;
  gap: 4px;
  margin-bottom: 18px;
}
.rt-tab {
  flex: 1;
  padding: 10px 14px;
  border: 0;
  background: transparent;
  cursor: pointer;
  font-size: 13px;
  font-weight: 700;
  color: var(--rt-ink-soft);
  border-radius: 7px;
  transition: 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  &.active {
    background: #fff;
    color: var(--rt-ink);
    box-shadow: 0 1px 2px rgba(14, 23, 38, 0.04);
  }
}

/* search */
.rt-search-row {
  display: grid;
  grid-template-columns: 1fr;
  gap: 14px;
}
.rt-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  label {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--rt-ink-soft);
  }
  .req {
    color: var(--rt-danger);
    margin-left: 2px;
  }
}
.rt-input-wrap {
  position: relative;
  display: flex;
  align-items: center;
  background: #fff;
  border: 1.5px solid var(--rt-line);
  border-radius: 10px;
  transition: 0.2s;
  &:focus-within {
    border-color: var(--rt-primary);
    box-shadow: 0 0 0 4px rgba(30, 74, 138, 0.08);
  }
  .lead {
    position: absolute;
    left: 14px;
    color: var(--rt-ink-muted);
    font-size: 14px;
  }
  input {
    flex: 1;
    border: 0;
    outline: none;
    padding: 12px 12px 12px 38px;
    background: transparent;
    font-size: 14px;
    font-weight: 500;
    color: var(--rt-ink);
  }
}
.rt-go {
  margin-right: 4px;
  background: var(--rt-primary);
  color: #fff;
  border: 0;
  padding: 8px 16px;
  border-radius: 7px;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: 0.2s;
  &:hover {
    background: var(--rt-primary-dark);
  }
}

/* found inspector */
.rt-found {
  margin-top: 18px;
  padding: 16px;
  background: #f8f6f0;
  border: 1px solid var(--rt-line);
  border-radius: 10px;
  display: flex;
  align-items: center;
  gap: 14px;
}
.rt-av {
  width: 48px;
  height: 48px;
  border-radius: 10px;
  background: var(--rt-ink);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 15px;
}
.rt-info {
  flex: 1;
  .rt-nm {
    font-weight: 700;
    font-size: 15px;
  }
  .rt-org {
    font-size: 12px;
    color: var(--rt-ink-soft);
    margin-top: 2px;
  }
}
.rt-badge {
  font-size: 11px;
  font-weight: 700;
  padding: 5px 10px;
  border-radius: 20px;
  background: #e8f1ea;
  color: var(--rt-success);
  display: flex;
  align-items: center;
  gap: 6px;
  &::before {
    content: "";
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--rt-success);
  }
}

/* inspection list */
.rt-insp-wrap {
  margin-top: 18px;
}
.rt-sublabel {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--rt-ink-soft);
  display: block;
  margin-bottom: 10px;
}
.rt-insp-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.rt-insp {
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: 14px;
  align-items: center;
  padding: 14px 16px;
  border: 1.5px solid var(--rt-line);
  border-radius: 10px;
  cursor: pointer;
  transition: 0.2s;
  background: #fff;
  &:hover {
    border-color: var(--rt-primary);
    background: #fafbfd;
  }
  &.selected {
    border-color: var(--rt-primary);
    background: #f2f6fc;
    box-shadow: 0 0 0 3px rgba(30, 74, 138, 0.08);
  }
}
.rt-radio {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 2px solid var(--rt-line);
  position: relative;
  flex-shrink: 0;
}
.rt-insp.selected .rt-radio {
  border-color: var(--rt-primary);
}
.rt-insp.selected .rt-radio::after {
  content: "";
  position: absolute;
  inset: 3px;
  border-radius: 50%;
  background: var(--rt-primary);
}
.rt-main {
  .rt-code {
    font-size: 15px;
    font-weight: 700;
  }
  .rt-desc {
    font-size: 12px;
    color: var(--rt-ink-soft);
    margin-top: 2px;
  }
}
.rt-date {
  text-align: right;
  .rt-d-label {
    display: block;
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: var(--rt-ink-muted);
  }
  .rt-d-v {
    font-weight: 700;
    color: var(--rt-ink);
    font-size: 13px;
  }
}

/* rating grid */
.rt-rate-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 18px;
}
.rt-rate-block {
  border: 1.5px solid var(--rt-line);
  border-radius: 12px;
  padding: 20px;
  background: #fff;
  display: flex;
  flex-direction: column;
  gap: 14px;
  position: relative;
  overflow: hidden;
  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: var(--rt-accent);
  }
}
.rt-who {
  display: flex;
  align-items: center;
  gap: 10px;
}
.rt-who-icon {
  width: 38px;
  height: 38px;
  border-radius: 8px;
  background: var(--rt-accent-soft);
  color: var(--rt-ink);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
}
.rt-who-lbl {
  font-size: 11px;
  color: var(--rt-ink-muted);
  letter-spacing: 0.08em;
  text-transform: uppercase;
  font-weight: 700;
}
.rt-who-nm {
  font-weight: 700;
  font-size: 14px;
  margin-top: 1px;
}

/* stars */
.rt-stars {
  display: flex;
  gap: 6px;
  align-items: center;
  padding: 6px 0;
}
.rt-star {
  width: 32px;
  height: 32px;
  border: 0;
  background: transparent;
  cursor: pointer;
  padding: 0;
  transition: 0.15s;
  color: #d8d6ce;
  font-size: 26px;
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover {
    transform: scale(1.15);
  }
  &.filled {
    color: var(--rt-accent);
  }
}
.rt-rate-label {
  font-size: 12px;
  color: var(--rt-ink-soft);
  font-weight: 500;
  display: flex;
  justify-content: space-between;
  strong {
    color: var(--rt-ink);
    font-weight: 700;
    font-size: 15px;
  }
}

/* comment */
.rt-comment {
  width: 100%;
  min-height: 90px;
  border: 1.5px solid var(--rt-line);
  border-radius: 8px;
  padding: 12px;
  font-size: 13px;
  font-weight: 500;
  color: var(--rt-ink);
  resize: vertical;
  background: #fbfaf6;
  transition: 0.2s;
  font-family: inherit;
  &:focus {
    outline: none;
    border-color: var(--rt-primary);
    background: #fff;
  }
}
.rt-cc {
  font-size: 11px;
  color: var(--rt-ink-muted);
  text-align: right;
  margin-top: 2px;
}

/* actions */
.rt-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 14px;
  padding: 18px 22px;
  background: #fafaf6;
  border-top: 1px solid var(--rt-line);
  flex-wrap: wrap;
}
.rt-note {
  font-size: 12px;
  color: var(--rt-ink-soft);
  display: flex;
  align-items: center;
  gap: 8px;
  i {
    color: var(--rt-ink-muted);
  }
}
.rt-btns {
  display: flex;
  gap: 10px;
}
.rt-btn {
  border: 0;
  cursor: pointer;
  font-size: 14px;
  font-weight: 700;
  padding: 11px 22px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: 0.2s;
  &.ghost {
    background: transparent;
    color: var(--rt-ink-soft);
    border: 1.5px solid var(--rt-line);
    &:hover {
      border-color: var(--rt-ink);
      color: var(--rt-ink);
    }
  }
  &.primary {
    background: var(--rt-ink);
    color: #fff;
    &:hover {
      background: #000;
    }
  }
}

@media (max-width: 768px) {
  .rt-page {
    padding: 16px 12px 60px;
  }
  .rt-title {
    font-size: 22px;
  }
  .rt-rate-grid {
    grid-template-columns: 1fr;
  }
  .rt-tabs {
    flex-direction: column;
  }
  .rt-actions {
    flex-direction: column;
    align-items: stretch;
    .rt-btns {
      justify-content: flex-end;
    }
  }
}
</style>
